import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * 移动端右下角悬浮：页面进度（滚动百分比）+ 一键返回顶端（毛玻璃）。
 * - /datasets 分类页改由 DatasetsMobileNav 接管（序号/总数 + 分类导航），此处返回 null 让位。
 * - 首页：hero 全屏期间隐藏，hero 滑出后显示；进入/消失走 opacity+transform 过渡，
 *   且常驻挂载（仅切换可见性类）以保证布局恒定、过渡平滑、不突兀。
 * - 其他页：常显「页面进度: N%」。
 */
interface Snapshot {
  pct: number; // 0..1 滚动进度
  scrolled: boolean; // 是否已下滚（决定是否显示返回顶端）
  heroCovering: boolean; // 首页 hero 是否仍基本占满视口（true 时隐藏指示器）
}

const INIT: Snapshot = { pct: 0, scrolled: false, heroCovering: false };

export function MobileViewProgress() {
  const location = useLocation();
  const onDatasets = location.pathname === '/datasets';
  const isHome = location.pathname === '/';
  const [snap, setSnap] = useState<Snapshot>(INIT);
  const ref = useRef<Snapshot>(INIT);

  useEffect(() => {
    let raf = 0;
    const compute = () => {
      raf = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const pct = max > 4 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      const scrolled = window.scrollY > 220;
      // 首页全屏 hero 期间隐藏指示器：内容卡升起覆盖 60% 视口后再显示。
      // hero 为 sticky 100svh，按其真实高度判定，降级时用视口高度兜底。
      let heroCovering = false;
      if (isHome) {
        const heroEl = document.querySelector('.zx-hero') as HTMLElement | null;
        const heroH = heroEl?.offsetHeight || window.innerHeight;
        heroCovering = window.scrollY < heroH * 0.6;
      }
      const next: Snapshot = { pct, scrolled, heroCovering };
      const p = ref.current;
      if (p.pct !== next.pct || p.scrolled !== next.scrolled || p.heroCovering !== next.heroCovering) {
        ref.current = next;
        setSnap(next);
      }
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(compute);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    compute();
    const iv = window.setInterval(compute, 700); // 兜底：异步内容高度变化
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.clearInterval(iv);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isHome]);

  // /datasets 分类页改由 DatasetsMobileNav 接管（进度球 + 一级分类导航），此处让位避免重复浮层。
  if (onDatasets) return null;

  const pct = Math.round(snap.pct * 100);
  // 首页 hero 全屏期间隐藏；hero 滑出后再显示。其他页恒显。
  const showProgress = !(isHome && snap.heroCovering);
  const showBackTop = snap.scrolled && showProgress;

  // 常驻挂载、仅切换可见性类 → 进入/消失平滑过渡（淡入淡出 + 轻微升降 + 缩放），布局恒定不跳动。
  const vis = (on: boolean) =>
    on
      ? 'pointer-events-auto opacity-100 translate-y-0 scale-100'
      : 'pointer-events-none opacity-0 translate-y-2 scale-95';

  return (
    <div className="pointer-events-none fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-4 z-40 flex flex-col items-end gap-2 lg:hidden">
      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="返回顶端"
        aria-hidden={!showBackTop}
        tabIndex={showBackTop ? 0 : -1}
        className={`liquid-glass liquid-glass-float flex h-11 w-11 origin-bottom-right items-center justify-center rounded-full border border-line text-fg-muted transition-[opacity,transform,color] duration-300 ease-out hover:text-navy ${vis(
          showBackTop,
        )}`}
      >
        <svg
          viewBox="0 0 24 24"
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M12 19V5" />
          <path d="m5 12 7-7 7 7" />
        </svg>
      </button>

      {/* 页面进度（滚动百分比）。首页 hero 全屏期间隐藏，hero 滑出后淡入。 */}
      <div
        role="status"
        aria-hidden={!showProgress}
        aria-label={`页面进度 ${pct}%`}
        className={`liquid-glass liquid-glass-float flex h-9 origin-bottom-right items-center rounded-full border border-line px-3 text-[11px] font-semibold tabular-nums text-fg transition-[opacity,transform] duration-300 ease-out ${vis(
          showProgress,
        )}`}
      >
        页面进度: {pct}%
      </div>
    </div>
  );
}
